
import { useState } from 'react'
import { Plus, Settings, AlertTriangle, Wrench, Trash2, Edit, Search } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Progress } from '@/components/ui/progress'
import { 
  useGetEquipmentQuery, 
  useCreateEquipmentMutation, 
  useUpdateEquipmentMutation,
  useDeleteEquipmentMutation 
} from '@/store/api'
import { Equipment } from '@/types'

import CreateEquipmentModal from './components/CreateEquipmentModal'
import EditEquipmentModal from './components/EditEquipmentModal'

export default function EquipmentPage() {
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [editingEquipment, setEditingEquipment] = useState<Equipment | null>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [typeFilter, setTypeFilter] = useState<string>('all')
  const [statusFilter, setStatusFilter] = useState<string>('all')

  const { data: equipmentResponse, isLoading, error } = useGetEquipmentQuery({})
  const [createEquipment] = useCreateEquipmentMutation()
  const [updateEquipment] = useUpdateEquipmentMutation()
  const [deleteEquipment] = useDeleteEquipmentMutation()

  const equipment = equipmentResponse?.data || []

  const filteredEquipment = equipment.filter(item => {
    const matchesSearch = item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (item.manufacturer && item.manufacturer.toLowerCase().includes(searchTerm.toLowerCase())) ||
                         (item.model && item.model.toLowerCase().includes(searchTerm.toLowerCase()))
    
    const matchesType = typeFilter === 'all' || item.type === typeFilter
    const matchesStatus = statusFilter === 'all' || item.status === statusFilter

    return matchesSearch && matchesType && matchesStatus
  })

  const handleCreateEquipment = async (equipmentData: Partial<Equipment>) => {
    try {
      await createEquipment(equipmentData).unwrap()
      setShowCreateModal(false)
    } catch (error) {
      if (import.meta.env.DEV) {
        console.debug('Failed to create equipment:', error)
      }
      // Error handling can be added here (e.g., toast notification)
    }
  }

  const handleUpdateEquipment = async (equipmentData: Partial<Equipment>) => {
    if (!editingEquipment) return
    
    try {
      await updateEquipment({ 
        id: editingEquipment.id, 
        updates: equipmentData 
      }).unwrap()
      setEditingEquipment(null)
    } catch (error) {
      if (import.meta.env.DEV) {
        console.debug('Failed to update equipment:', error)
      }
      // Error handling can be added here (e.g., toast notification)
    }
  }

  const handleDeleteEquipment = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this equipment? This action cannot be undone.')) {
      try {
        await deleteEquipment(id).unwrap()
      } catch (error) {
        if (import.meta.env.DEV) {
          console.debug('Failed to delete equipment:', error)
        }
        // Error handling can be added here (e.g., toast notification)
      }
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'operational':
        return 'bg-green-100 text-green-800'
      case 'maintenance':
        return 'bg-yellow-100 text-yellow-800'
      case 'offline':
        return 'bg-red-100 text-red-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }


  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'hvac':
        return <Settings className="w-5 h-5" />
      case 'plumbing':
        return <Wrench className="w-5 h-5" />
      case 'electrical':
        return <AlertTriangle className="w-5 h-5" />
      default:
        return <Settings className="w-5 h-5" />
    }
  }

  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-48 mb-6"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5, 6].map(i => (
              <div key={i} className="h-64 bg-gray-200 rounded-lg"></div>
            ))}
          </div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center py-12">
          <p className="text-red-600">Failed to load equipment. Please try again.</p>
        </div>
      </div>
    )
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Equipment</h1>
          <p className="text-gray-600 mt-1">Monitor equipment health and performance</p>
        </div>
        <Button onClick={() => setShowCreateModal(true)} className="flex items-center gap-2">
          <Plus className="w-4 h-4" />
          Add Equipment
        </Button>
      </div>

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg border border-gray-200 mb-6">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <Input
              placeholder="Search equipment..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          <div className="flex gap-2">
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="px-3 py-2 border rounded-md bg-white min-w-[120px]"
            >
              <option value="all">All Types</option>
              <option value="hvac">HVAC</option>
              <option value="plumbing">Plumbing</option>
              <option value="electrical">Electrical</option>
              <option value="elevators">Elevators</option>
              <option value="security">Security</option>
              <option value="other">Other</option>
            </select>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-2 border rounded-md bg-white min-w-[120px]"
            >
              <option value="all">All Status</option>
              <option value="operational">Operational</option>
              <option value="maintenance">Maintenance</option>
              <option value="offline">Offline</option>
            </select>
          </div>
        </div>
      </div>

      {/* Equipment Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredEquipment.map((item) => (
          <Card key={item.id} className="hover:shadow-lg transition-shadow">
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    {getTypeIcon(item.type || 'other')}
                  </div>
                  <div>
                    <CardTitle className="text-lg">{item.name}</CardTitle>
                    <Badge className={getStatusColor(item.status || 'operational')}>
                      {item.status || 'operational'}
                    </Badge>
                  </div>
                </div>
                <div className="flex gap-1">
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => setEditingEquipment(item)}
                  >
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => handleDeleteEquipment(item.id)}
                    className="text-red-600 hover:text-red-700"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {/* Health Score */}
                <div>
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-gray-600">Health Score</span>
                    <span className="font-medium">{item.healthScore || item.health_score || 0}%</span>
                  </div>
                  <Progress 
                    value={item.healthScore || item.health_score || 0} 
                    className="h-2"
                    style={{
                      background: '#f3f4f6'
                    }}
                  />
                </div>
                
                {/* Equipment Details */}
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Type:</span>
                    <span className="font-medium capitalize">{item.type}</span>
                  </div>
                  {item.manufacturer && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Manufacturer:</span>
                      <span className="font-medium">{item.manufacturer}</span>
                    </div>
                  )}
                  {item.model && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Model:</span>
                      <span className="font-medium">{item.model}</span>
                    </div>
                  )}
                </div>
                
                {/* Stats */}
                <div className="grid grid-cols-2 gap-4 pt-3 border-t">
                  <div className="text-center">
                    <div className="text-lg font-bold text-orange-600">
                      {item.sensors?.length || 0}
                    </div>
                    <div className="text-xs text-gray-600">Sensors</div>
                  </div>
                  <div className="text-center">
                    <div className="text-lg font-bold text-red-600">
                      {item.activeAlerts || item.active_alerts?.[0]?.count || 0}
                    </div>
                    <div className="text-xs text-gray-600">Alerts</div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {filteredEquipment.length === 0 && (
        <div className="text-center py-12">
          <Settings className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No equipment found</h3>
          <p className="text-gray-600 mb-4">
            {searchTerm || typeFilter !== 'all' || statusFilter !== 'all'
              ? 'Try adjusting your search terms or filters.' 
              : 'Get started by adding your first piece of equipment.'}
          </p>
          {!searchTerm && typeFilter === 'all' && statusFilter === 'all' && (
            <Button onClick={() => setShowCreateModal(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Add Your First Equipment
            </Button>
          )}
        </div>
      )}

      {/* Modals */}
      <CreateEquipmentModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSubmit={handleCreateEquipment}
      />

      {editingEquipment && (
        <EditEquipmentModal
          isOpen={true}
          equipment={editingEquipment}
          onClose={() => setEditingEquipment(null)}
          onSubmit={handleUpdateEquipment}
        />
      )}
    </div>
  )
}